/**
 * CommandPalette.tsx — ⌘K / Ctrl+K
 * Recherche fuzzy de commandes, calques, templates, polices.
 * Porté depuis snapbooth-tempalte/project/command-palette.jsx.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Download,
  HelpCircle,
  Type,
  Image as ImageIcon,
  Square as SquareIcon,
  Grid3X3,
  RotateCcw,
  RotateCw,
  Copy,
  Trash2,
  Maximize2,
  ZoomIn,
  Moon,
  Sparkles,
  LayoutGrid,
  Eye,
} from 'lucide-react';
import { useAppStore } from '../../../services/StateService';
import { useToast } from '../common/ToastProvider';
import { logger } from '../../../utils/ProfessionalLogger';

export type CommandAction =
  | 'open-export'
  | 'open-share'
  | 'open-shortcuts'
  | 'open-onboarding'
  | 'add-text'
  | 'add-image'
  | 'add-shape'
  | 'toggle-grid'
  | 'toggle-safezone'
  | 'toggle-theme'
  | 'undo'
  | 'redo'
  | 'duplicate'
  | 'delete'
  | 'zoom-fit'
  | 'zoom-100'
  | 'goto-dashboard'
  | 'goto-editor'
  | 'apply-template'
  | 'apply-font'
  | 'select-layer'
  | 'open-help'
  | 'contact'
  | 'ai-resize'
  | 'ai-text'
  | 'open-compare';

interface CommandItem {
  id: string;
  group: 'recent' | 'command' | 'layer' | 'template' | 'font' | 'help' | 'nav';
  label: string;
  sub?: string;
  icon: React.ComponentType<{ size?: number }>;
  shortcut?: string;
  iconBg?: string;
  tag?: string;
  featured?: boolean;
  recent?: boolean;
  keywords?: string[];
  action: CommandAction;
  payload?: unknown;
}

const GROUP_LABELS: Record<CommandItem['group'], string> = {
  recent: 'Récents',
  command: 'Commandes',
  layer: 'Calques du template',
  template: 'Modèles',
  font: 'Polices',
  help: 'Aide',
  nav: 'Navigation',
};

function buildItems(): CommandItem[] {
  return [
    /* ─── Navigation ─── */
    {
      id: 'nav-dash',
      group: 'nav',
      label: 'Aller au tableau de bord',
      icon: LayoutGrid,
      shortcut: 'G D',
      action: 'goto-dashboard',
      featured: true,
    },
    {
      id: 'nav-editor',
      group: 'nav',
      label: "Aller à l'éditeur",
      icon: Eye,
      shortcut: 'G E',
      action: 'goto-editor',
    },

    /* ─── Commandes principales ─── */
    {
      id: 'cmd-export',
      group: 'command',
      label: 'Exporter le template…',
      sub: 'Imprimer, partager, télécharger',
      icon: Download,
      shortcut: '⌘ E',
      iconBg: '#7C3AED',
      featured: true,
      action: 'open-export',
    },
    {
      id: 'cmd-shortcuts',
      group: 'command',
      label: 'Raccourcis clavier',
      icon: HelpCircle,
      shortcut: '⌘ /',
      action: 'open-shortcuts',
    },
    {
      id: 'cmd-add-text',
      group: 'command',
      label: 'Ajouter un texte',
      icon: Type,
      shortcut: 'T',
      featured: true,
      action: 'add-text',
    },
    {
      id: 'cmd-add-image',
      group: 'command',
      label: 'Importer une image',
      icon: ImageIcon,
      shortcut: '⌘ U',
      featured: true,
      keywords: ['upload', 'photo', 'logo'],
      action: 'add-image',
    },
    {
      id: 'cmd-add-shape',
      group: 'command',
      label: 'Ajouter une forme',
      icon: SquareIcon,
      shortcut: 'R',
      action: 'add-shape',
    },
    {
      id: 'cmd-grid',
      group: 'command',
      label: 'Afficher/masquer la grille',
      icon: Grid3X3,
      shortcut: '⇧ G',
      action: 'toggle-grid',
    },
    {
      id: 'cmd-safezone',
      group: 'command',
      label: 'Zone de sécurité',
      icon: SquareIcon,
      shortcut: '⇧ S',
      action: 'toggle-safezone',
    },
    {
      id: 'cmd-undo',
      group: 'command',
      label: 'Annuler',
      icon: RotateCcw,
      shortcut: '⌘ Z',
      action: 'undo',
    },
    {
      id: 'cmd-redo',
      group: 'command',
      label: 'Rétablir',
      icon: RotateCw,
      shortcut: '⌘ ⇧ Z',
      action: 'redo',
    },
    {
      id: 'cmd-duplicate',
      group: 'command',
      label: 'Dupliquer la sélection',
      icon: Copy,
      shortcut: '⌘ D',
      action: 'duplicate',
    },
    {
      id: 'cmd-delete',
      group: 'command',
      label: 'Supprimer la sélection',
      icon: Trash2,
      shortcut: '⌫',
      action: 'delete',
    },
    {
      id: 'cmd-zoom-fit',
      group: 'command',
      label: "Adapter à l'écran",
      icon: Maximize2,
      shortcut: '⌘ 0',
      action: 'zoom-fit',
    },
    {
      id: 'cmd-zoom-100',
      group: 'command',
      label: 'Zoom 100 %',
      icon: ZoomIn,
      shortcut: '⌘ 1',
      action: 'zoom-100',
    },
    {
      id: 'cmd-theme',
      group: 'command',
      label: 'Basculer thème clair/sombre',
      icon: Moon,
      action: 'toggle-theme',
    },
    {
      id: 'cmd-compare',
      group: 'command',
      label: 'Comparer deux versions',
      sub: 'Side-by-side',
      icon: LayoutGrid,
      shortcut: '⌘ ⇧ K',
      action: 'open-compare',
    },

    /* ─── Aide ─── */
    {
      id: 'help-tour',
      group: 'help',
      label: 'Refaire le tour guidé',
      icon: Sparkles,
      action: 'open-onboarding',
    },
    {
      id: 'help-docs',
      group: 'help',
      label: "Centre d'aide",
      icon: HelpCircle,
      sub: 'Articles & vidéos',
      action: 'open-help',
    },
  ];
}

function matchScore(item: CommandItem, q: string): number {
  const haystack = [item.label, item.sub, item.group, ...(item.keywords || [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!haystack.includes(q[0])) return 0;
  let score = 0;
  if (item.label.toLowerCase().startsWith(q)) score += 100;
  if (item.label.toLowerCase().includes(q)) score += 50;
  if (haystack.includes(q)) score += 20;
  let idx = 0;
  for (const ch of q) {
    const found = haystack.indexOf(ch, idx);
    if (found < 0) return 0;
    idx = found + 1;
  }
  score += 5;
  if (item.recent) score += 10;
  return score;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  // Lecture du store via getState() pour avoir les dernières actions sans re-render.
  const items = useMemo(() => buildItems(), []);

  const filtered = useMemo(() => {
    if (!query.trim()) {
      return items.filter(i => i.featured).slice(0, 12);
    }
    const q = query.toLowerCase();
    return items
      .map(i => ({ ...i, _score: matchScore(i, q) }))
      .filter(i => i._score > 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, 14);
  }, [items, query]);

  // Reset état à l'ouverture
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  /**
   * Exécute l'action d'un item — on traite les principales et on log les autres
   * comme des "TODO câblage" pour ne pas bloquer la palette.
   */
  const execute = useCallback(
    (item: CommandItem) => {
      // P3 redesign : actions câblées au vrai store + fallback CustomEvent.
      // L'éditeur peut écouter `snapbooth:cmd` pour les actions non câblées ici.
      const store = useAppStore.getState() as unknown as {
        undo?: () => void;
        redo?: () => void;
        addShape?: (shape: Record<string, unknown>) => void;
        deleteShape?: (id: string) => void;
        duplicateShape?: (id: string) => void;
        setZoomLevel?: (level: number) => void;
        setGridEnabled?: (enabled: boolean) => void;
        setSafeZoneVisible?: (visible: boolean) => void;
        setShowAssetsLibrary?: (show: boolean) => void;
        selection?: { primaryId: string | null; selectedIds: string[] };
        canvas?: { gridEnabled?: boolean; zoomLevel?: number };
        project?: { safeZoneVisible?: boolean };
      };

      switch (item.action) {
        /* ─── Navigation ─── */
        case 'goto-dashboard':
          navigate('/dashboard');
          break;
        case 'goto-editor':
          navigate('/editor');
          break;

        /* ─── Undo / Redo (déjà câblés dans le store via middleware) ─── */
        case 'undo':
          if (typeof store.undo === 'function') store.undo();
          else addToast({ type: 'info', message: 'Annuler (non disponible)' });
          break;
        case 'redo':
          if (typeof store.redo === 'function') store.redo();
          else addToast({ type: 'info', message: 'Rétablir (non disponible)' });
          break;

        /* ─── Delete sélection ─── */
        case 'delete': {
          const id = store.selection?.primaryId;
          if (id && typeof store.deleteShape === 'function') {
            store.deleteShape(id);
            addToast({ type: 'success', message: 'Élément supprimé' });
          } else {
            addToast({ type: 'info', message: 'Aucune sélection' });
          }
          break;
        }

        /* ─── Zoom (vrais setters du store) ─── */
        case 'zoom-fit':
          if (typeof store.setZoomLevel === 'function') store.setZoomLevel(0.8);
          break;
        case 'zoom-100':
          if (typeof store.setZoomLevel === 'function') store.setZoomLevel(1);
          break;

        /* ─── Grid toggle (vrai store) ─── */
        case 'toggle-grid':
          if (typeof store.setGridEnabled === 'function') {
            const current = store.canvas?.gridEnabled ?? false;
            store.setGridEnabled(!current);
            addToast({ type: 'info', message: !current ? 'Grille affichée' : 'Grille masquée' });
          }
          break;

        /* ─── Theme (DOM + localStorage) ─── */
        case 'toggle-theme': {
          const root = document.documentElement;
          const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
          root.setAttribute('data-theme', next);
          try {
            localStorage.setItem('snapbooth-theme', next);
          } catch {
            /* noop */
          }
          addToast({ type: 'info', message: `Thème : ${next === 'dark' ? 'sombre' : 'clair'}` });
          break;
        }

        /* ─── Add shape (texte, rect, image placeholder) ─── */
        case 'add-text':
          if (typeof store.addShape === 'function') {
            store.addShape({
              id: `text-${Date.now()}`,
              type: 'text',
              text: 'Nouveau texte',
              x: 100,
              y: 100,
              width: 220,
              height: 48,
              fontSize: 24,
              fontFamily: 'Plus Jakarta Sans',
              fill: '#1A1530',
            });
            addToast({ type: 'success', message: 'Texte ajouté' });
          }
          break;
        case 'add-shape':
          if (typeof store.addShape === 'function') {
            store.addShape({
              id: `rect-${Date.now()}`,
              type: 'rect',
              x: 120,
              y: 120,
              width: 160,
              height: 160,
              fill: '#7C3AED',
            });
            addToast({ type: 'success', message: 'Rectangle ajouté' });
          }
          break;

        /* ─── Dupliquer la sélection (store) ─── */
        case 'duplicate': {
          const id = store.selection?.primaryId;
          if (id && typeof store.duplicateShape === 'function') {
            store.duplicateShape(id);
            addToast({ type: 'success', message: 'Élément dupliqué' });
          } else {
            addToast({ type: 'info', message: 'Aucune sélection' });
          }
          break;
        }

        /* ─── Zone de sécurité (store) ─── */
        case 'toggle-safezone':
          if (typeof store.setSafeZoneVisible === 'function') {
            const current = store.project?.safeZoneVisible ?? true;
            store.setSafeZoneVisible(!current);
            addToast({
              type: 'info',
              message: !current ? 'Zone de sécurité affichée' : 'Zone de sécurité masquée',
            });
          }
          break;

        /* ─── Ajouter une image → bibliothèque d'assets (store) ─── */
        case 'add-image':
          if (typeof store.setShowAssetsLibrary === 'function') {
            store.setShowAssetsLibrary(true);
          } else {
            window.dispatchEvent(new CustomEvent('snapbooth:open-assets'));
          }
          break;

        /* ─── Modales (écoutées par SnapboothTopbar / useModals) ─── */
        case 'open-export':
          window.dispatchEvent(new CustomEvent('snapbooth:open-export'));
          break;
        case 'open-help':
          window.dispatchEvent(new CustomEvent('show-user-guide'));
          break;
        case 'open-shortcuts':
          window.dispatchEvent(new CustomEvent('show-shortcuts'));
          break;
        case 'open-onboarding':
          window.dispatchEvent(new CustomEvent('show-onboarding'));
          break;

        /* ─── Comparer les variantes de layout → vue Variations ─── */
        case 'open-compare':
          navigate('/variations');
          break;

        default:
          logger.warn('[CommandPalette] action non câblée :', item.action);
      }
    },
    [navigate, addToast]
  );

  // Navigation clavier
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx(i => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx(i => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[activeIdx];
        if (item) {
          execute(item);
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, filtered, activeIdx, onClose, execute]);

  if (!open) return null;

  // Grouper pour affichage
  const groups: Record<string, Array<CommandItem & { _idx: number }>> = {};
  filtered.forEach((item, i) => {
    if (!groups[item.group]) groups[item.group] = [];
    groups[item.group].push({ ...item, _idx: i });
  });

  return (
    <div
      className="sb-cmd-overlay"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sb-cmd-shell" role="dialog" aria-label="Palette de commandes">
        {/* Search */}
        <div className="sb-cmd-search">
          <Search size={18} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="sb-cmd-input"
            placeholder="Chercher une commande, un calque, un template…"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="sb-kbd">Échap</kbd>
        </div>

        {/* Results */}
        <div className="sb-cmd-results">
          {filtered.length === 0 && (
            <div className="sb-cmd-empty">
              <div className="sb-cmd-empty-emoji">🔍</div>
              <div className="sb-cmd-empty-title">Rien trouvé pour « {query} »</div>
              <div className="sb-cmd-empty-sub">
                Essayez « exporter », « grille », « zoom » ou « thème »
              </div>
            </div>
          )}
          {Object.entries(groups).map(([group, list]) => (
            <div key={group} className="sb-cmd-group">
              <div className="sb-cmd-group-title">
                {GROUP_LABELS[group as CommandItem['group']] || group}
              </div>
              {list.map(item => {
                const Ico = item.icon;
                const iconStyle = item.iconBg
                  ? { background: item.iconBg, color: 'white' }
                  : undefined;
                return (
                  <div
                    key={item.id}
                    className={`sb-cmd-item ${item._idx === activeIdx ? 'is-active' : ''}`}
                    onMouseEnter={() => setActiveIdx(item._idx)}
                    onClick={() => {
                      execute(item);
                      onClose();
                    }}
                  >
                    <div className="sb-cmd-item-icon" style={iconStyle}>
                      <Ico size={15} />
                    </div>
                    <div className="sb-cmd-item-body">
                      <div className="sb-cmd-item-label">{item.label}</div>
                      {item.sub && <div className="sb-cmd-item-sub">{item.sub}</div>}
                    </div>
                    {item.shortcut && (
                      <span className="sb-cmd-item-shortcut">
                        {item.shortcut.split(' ').map((k, i) => (
                          <kbd key={i} className="sb-kbd">
                            {k}
                          </kbd>
                        ))}
                      </span>
                    )}
                    {item.tag && <span className="sb-cmd-tag">{item.tag}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sb-cmd-foot">
          <div className="sb-cmd-foot-hints">
            <span>
              <kbd className="sb-kbd">↑</kbd>
              <kbd className="sb-kbd">↓</kbd> Naviguer
            </span>
            <span>
              <kbd className="sb-kbd">↵</kbd> Ouvrir
            </span>
            <span>
              <kbd className="sb-kbd">Échap</kbd> Fermer
            </span>
          </div>
          <div className="sb-cmd-foot-brand">
            <Sparkles size={11} />
            Snapbooth IA
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook global : enregistre ⌘K / Ctrl+K et expose `open`/`setOpen`.
 * À monter UNE seule fois au plus haut niveau (App.tsx ou AppRouter.tsx).
 */
export function useCommandPaletteGlobalShortcut(): {
  open: boolean;
  setOpen: (v: boolean) => void;
} {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen };
}
